import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useDelayedFlag } from '../../components/ui';
import { sbg } from '../../theme/sbgTokens';

const card = {
  bgcolor: sbg.bg.surface,
  border: `1px solid ${sbg.border.default}`,
  borderRadius: sbg.radius.card,
  p: 3,
  display: 'grid',
  gap: 1.5,
  alignContent: 'start',
} as const;

/**
 * Loading skeleton shaped like the record: avatar header, tabs, the balances card and the
 * two-column overview. Revealed after 300ms so a fast load never flashes.
 */
const RecordSkeleton: React.FC<{ label: string }> = ({ label }) => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label={label} aria-busy="true" sx={{ width: '100%', visibility: visible ? 'visible' : 'hidden' }}>
      <span className="sbg-sr-only">{label}</span>
      <Box aria-hidden="true">
        {/* Header: avatar, overline, name, meta, actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box sx={{ flex: '1 1 240px', display: 'grid', gap: 1 }}>
            <Skeleton variant="rounded" width={72} height={14} />
            <Skeleton variant="rounded" width="min(320px, 70%)" height={30} />
            <Skeleton variant="rounded" width="min(460px, 90%)" height={16} />
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            <Skeleton variant="rounded" width={128} height={36} sx={{ borderRadius: sbg.radius.pill }} />
            <Skeleton variant="rounded" width={168} height={36} sx={{ borderRadius: sbg.radius.pill }} />
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ display: 'flex', gap: 3, pb: 1.5, mb: 3, borderBottom: `1px solid ${sbg.border.subtle}` }}>
          {[72, 84, 128].map((width) => (
            <Skeleton key={width} variant="rounded" width={width} height={18} />
          ))}
        </Box>

        {/* Balances */}
        <Box sx={{ ...card, mb: 3 }}>
          <Skeleton variant="rounded" width={200} height={22} />
          <Skeleton variant="rounded" width="min(420px, 80%)" height={16} />
          {/* PTO and sick time are parallel: equal columns, same figure height (numericLg = 40px). */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, columnGap: 4, rowGap: 3, mt: 2 }}>
            {['pto', 'sick'].map((panel) => (
              <Box key={panel} sx={{ borderTop: `1px solid ${sbg.border.default}`, pt: 2, display: 'grid', gap: 1.5 }}>
                <Skeleton variant="rounded" width="28%" height={14} />
                <Skeleton variant="rounded" width="44%" height={40} />
                <Skeleton variant="rounded" width="62%" height={16} />
                <Skeleton variant="rounded" width="100%" height={8} />
                <Skeleton variant="rounded" width="88%" height={36} />
              </Box>
            ))}
          </Box>
        </Box>

        {/* Overview columns */}
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(300px, 1fr)' } }}>
          <Box sx={{ ...card, minHeight: 220 }}>
            <Skeleton variant="rounded" width={180} height={22} />
            <Skeleton variant="rounded" width="60%" height={16} />
            <Skeleton variant="rounded" width="100%" height={88} sx={{ mt: 1 }} />
          </Box>
          <Box sx={{ ...card, minHeight: 220 }}>
            <Skeleton variant="rounded" width={160} height={22} />
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="rounded" width={`${88 - row * 12}%`} height={36} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default RecordSkeleton;
